'use client';

import React from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { CheckCircle2, XCircle, Clock, Printer, Sparkles, ShieldAlert } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * SmartCardField
 * Definition for a dynamic field rendered in a SmartCard.
 */
export type SmartCardField = {
  id: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'date' | 'textarea' | 'time' | 'boolean' | 'readonly' | 'badge';
  options?: string[];
  required?: boolean;
  value?: string | number | boolean | null;
  unit?: string;
  description?: string;
};

/**
 * SmartCardRecipe
 * Structured schema representing an actionable operation generated by Heather.
 */
export type SmartCardRecipe = {
  title: string;
  description?: string;
  fields?: SmartCardField[];
  actions?: { id: string; label: string; type?: string }[];
  status?: string;
  type?: string;
  target_service?: string;
  method?: string;
  params?: Record<string, unknown>;
  [key: string]: unknown;
};

/**
 * SmartCardUIProps
 */
type SmartCardUIProps = {
  recipe: SmartCardRecipe;
  onAction?: (actionId: string, values: Record<string, any>) => void;
  disabled?: boolean;
  onValuesChange?: (values: Record<string, any>) => void;
  hideActions?: boolean;
  actionBy?: string;
  actionTimestamp?: number;
};

/**
 * SmartCardUI — Dynamic form and actionable receipt renderer for Heather workflows.
 * Adheres to Vibrant Industrial: Deep Dark (`bg-zinc-950`), h-8 density, semantic Emerald/Rose actions.
 */
export const SmartCardUI: React.FC<SmartCardUIProps> = ({
  recipe,
  onAction,
  disabled,
  onValuesChange,
  hideActions,
  actionBy,
  actionTimestamp
}) => {
  const isFinalized = recipe.status === 'approved' || recipe.status === 'rejected' || recipe.status === 'completed';
  const isExecuting = recipe.status === 'executing';
  const isConflict = recipe.status === 'conflict';

  // Form state for field values
  const [values, setValues] = React.useState<Record<string, any>>(() => {
    const initial: Record<string, any> = {};
    (recipe.fields || []).forEach(f => {
      initial[f.id] = f.value ?? (f.type === 'boolean' ? false : '');
    });
    return initial;
  });

  // Emit initial values on mount
  const mounted = React.useRef(false);
  React.useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      if (onValuesChange) onValuesChange(values);
    }
  }, [onValuesChange, values]);

  const handleChange = (id: string, value: any) => {
    const newValues = { ...values, [id]: value };
    setValues(newValues);
    if (onValuesChange) onValuesChange(newValues);
  };

  const handleAction = (actionId: string) => {
    if (onAction) onAction(actionId, values);
  };

  const renderStatusBadge = () => {
    if (!recipe.status || recipe.status === 'draft') {
      return (
        <div className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">
          <Clock className="w-3 h-3 text-amber-400" />
          Review Draft
        </div>
      );
    }

    if (isExecuting) {
      return (
        <div className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 animate-pulse">
          <Sparkles className="w-3 h-3 text-cyan-400" />
          Applying...
        </div>
      );
    }

    if (recipe.status === 'approved' || recipe.status === 'completed') {
      return (
        <div className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
          <CheckCircle2 className="w-3 h-3 text-emerald-400" />
          Approved
        </div>
      );
    }

    if (recipe.status === 'rejected') {
      return (
        <div className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded bg-rose-500/20 text-rose-300 border border-rose-500/40">
          <XCircle className="w-3 h-3 text-rose-400" />
          Rejected
        </div>
      );
    }

    if (isConflict) {
      return (
        <div className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded bg-orange-500/20 text-orange-300 border border-orange-500/40">
          <ShieldAlert className="w-3 h-3 text-orange-400" />
          Stale Data
        </div>
      );
    }

    return (
      <div className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-white/10 text-zinc-300 border border-white/10">
        {recipe.status}
      </div>
    );
  };

  const getActionStyle = (action: { id: string; label: string; type?: string }) => {
    const actId = action.id.toLowerCase();
    const actType = (action.type || '').toLowerCase();

    if (actId.includes('approve') || actId.includes('execute') || actId.includes('create') || actType === 'primary' || actType === 'submit') {
      return 'bg-emerald-600 hover:bg-emerald-500 text-white font-bold shadow-md shadow-emerald-950/40 border border-emerald-500/40';
    }
    if (actId.includes('reject') || actId.includes('cancel') || actType === 'destructive') {
      return 'bg-rose-600/20 hover:bg-rose-600/30 text-rose-300 border border-rose-500/40 font-semibold';
    }
    if (actId.includes('print') || actType === 'print') {
      return 'bg-indigo-600 hover:bg-indigo-500 text-white font-semibold border border-indigo-400/30';
    }
    return 'bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-white/10 font-semibold';
  };

  const isFormDisabled = disabled || isFinalized || isExecuting;

  return (
    <Card className="w-full bg-zinc-950/80 border border-white/10 rounded-xl p-3 shadow-xl backdrop-blur-md">
      <CardHeader className="px-0 pt-0 pb-2.5 flex flex-row justify-between items-start gap-3 border-b border-white/5">
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-black tracking-wide text-zinc-100 uppercase">{recipe.title}</h2>
          {recipe.description && (
            <p className="text-xs text-zinc-400 mt-0.5 whitespace-pre-line leading-relaxed">
              {recipe.description}
            </p>
          )}
        </div>
        <div className="shrink-0">{renderStatusBadge()}</div>
      </CardHeader>

      <CardContent className="px-0 pt-2.5 pb-0">
        {/* Field inputs / Dynamic renderer */}
        {recipe.fields && recipe.fields.length > 0 && (
          <form className="space-y-2.5" onSubmit={(e) => e.preventDefault()}>
            {recipe.fields.map((field, index) => {
              const fieldVal = values[field.id];
              return (
                <div key={`${field.id}-${index}`} className="flex flex-col gap-1">
                  <div className="flex items-center justify-between">
                    <label htmlFor={field.id} className="text-[11px] font-bold uppercase tracking-wider text-zinc-300">
                      {field.label}
                      {field.required && <span className="text-rose-400 ml-1">*</span>}
                    </label>
                    {field.unit && (
                      <span className="text-[10px] font-mono text-zinc-500 uppercase">{field.unit}</span>
                    )}
                  </div>

                  {/* Readonly / Badge Field */}
                  {(field.type === 'readonly' || field.type === 'badge') && (
                    <div className="h-8 px-2.5 rounded-lg bg-white/5 border border-white/10 flex items-center justify-between text-xs text-zinc-200 font-medium">
                      <span>{String(fieldVal ?? field.value ?? '—')}</span>
                      {field.unit && <span className="text-[10px] text-zinc-400 font-mono">{field.unit}</span>}
                    </div>
                  )}

                  {/* Text Input */}
                  {field.type === 'text' && (
                    <input
                      id={field.id}
                      type="text"
                      value={fieldVal ?? ''}
                      disabled={isFormDisabled}
                      onChange={(e) => handleChange(field.id, e.target.value)}
                      placeholder={field.description || `Enter ${field.label}...`}
                      className="h-8 w-full rounded-lg border border-white/10 bg-zinc-900/90 px-2.5 py-1 text-xs text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed"
                    />
                  )}

                  {/* Number Input */}
                  {field.type === 'number' && (
                    <div className="relative flex items-center">
                      <input
                        id={field.id}
                        type="number"
                        step="any"
                        value={fieldVal ?? ''}
                        disabled={isFormDisabled}
                        onChange={(e) => handleChange(field.id, e.target.value)}
                        placeholder="0.00"
                        className="h-8 w-full rounded-lg border border-white/10 bg-zinc-900/90 px-2.5 py-1 text-xs text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed pr-12 font-mono"
                      />
                      {field.unit && (
                        <span className="absolute right-2.5 text-[10px] font-bold text-zinc-500 pointer-events-none">
                          {field.unit}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Select Dropdown */}
                  {field.type === 'select' && field.options && (
                    <select
                      id={field.id}
                      value={fieldVal ?? ''}
                      disabled={isFormDisabled}
                      onChange={(e) => handleChange(field.id, e.target.value)}
                      className="h-8 w-full rounded-lg border border-white/10 bg-zinc-900/90 px-2 text-xs text-zinc-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <option value="" className="bg-zinc-900 text-zinc-500">Select option...</option>
                      {field.options.map((opt, i) => (
                        <option key={`${opt}-${i}`} value={opt} className="bg-zinc-900 text-zinc-100">
                          {opt}
                        </option>
                      ))}
                    </select>
                  )}

                  {/* Date Input */}
                  {field.type === 'date' && (
                    <input
                      id={field.id}
                      type="date"
                      value={fieldVal ?? ''}
                      disabled={isFormDisabled}
                      onChange={(e) => handleChange(field.id, e.target.value)}
                      className="h-8 w-full rounded-lg border border-white/10 bg-zinc-900/90 px-2.5 text-xs text-zinc-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed [color-scheme:dark]"
                    />
                  )}

                  {/* Time Input */}
                  {field.type === 'time' && (
                    <input
                      id={field.id}
                      type="time"
                      value={fieldVal ?? ''}
                      disabled={isFormDisabled}
                      onChange={(e) => handleChange(field.id, e.target.value)}
                      className="h-8 w-full rounded-lg border border-white/10 bg-zinc-900/90 px-2.5 text-xs text-zinc-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed [color-scheme:dark]"
                    />
                  )}

                  {/* Boolean / Toggle Switch */}
                  {field.type === 'boolean' && (
                    <button
                      type="button"
                      disabled={isFormDisabled}
                      onClick={() => handleChange(field.id, !fieldVal)}
                      className={cn(
                        "h-8 px-3 rounded-lg border flex items-center justify-between text-xs font-semibold transition-all",
                        fieldVal
                          ? "bg-emerald-950/60 border-emerald-500/50 text-emerald-300"
                          : "bg-zinc-900 border-white/10 text-zinc-400"
                      )}
                    >
                      <span>{field.description || field.label}</span>
                      <span className={cn(
                        "text-[10px] font-black uppercase px-2 py-0.5 rounded",
                        fieldVal ? "bg-emerald-600 text-white" : "bg-zinc-800 text-zinc-500"
                      )}>
                        {fieldVal ? 'YES' : 'NO'}
                      </span>
                    </button>
                  )}

                  {/* Textarea */}
                  {field.type === 'textarea' && (
                    <textarea
                      id={field.id}
                      value={fieldVal ?? ''}
                      disabled={isFormDisabled}
                      onChange={(e) => handleChange(field.id, e.target.value)}
                      placeholder={field.description || 'Add notes or details...'}
                      className="min-h-[56px] w-full rounded-lg border border-white/10 bg-zinc-900/90 px-2.5 py-1.5 text-xs text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed leading-normal"
                    />
                  )}
                </div>
              );
            })}
          </form>
        )}

        {/* Audit Receipt Banner */}
        {isFinalized && (
          <div className="mt-2.5 pt-2 border-t border-white/5 flex items-center justify-between text-[11px] text-zinc-400 font-mono">
            <span className="flex items-center gap-1.5">
              {recipe.status === 'approved' ? (
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
              ) : (
                <XCircle className="w-3.5 h-3.5 text-rose-400" />
              )}
              {recipe.status === 'approved' ? 'Action Completed' : 'Action Rejected'}
              {actionBy && <span className="text-zinc-300 font-sans">by {actionBy}</span>}
            </span>
            {actionTimestamp && (
              <span>{new Date(actionTimestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            )}
          </div>
        )}

        {/* Action Buttons */}
        {recipe.actions && !hideActions && !isFinalized && (
          <div className="flex flex-wrap items-center gap-2 mt-3 pt-2.5 border-t border-white/5">
            {recipe.actions.map((action, index) => (
              <button
                key={`${action.id}-${index}`}
                type="button"
                disabled={isFormDisabled}
                onClick={() => handleAction(action.id)}
                className={cn(
                  "inline-flex items-center justify-center rounded-lg text-xs uppercase tracking-wider px-3.5 py-1.5 transition-all active:scale-95 disabled:pointer-events-none disabled:opacity-50",
                  getActionStyle(action)
                )}
              >
                {action.id.toLowerCase().includes('print') && <Printer className="w-3.5 h-3.5 mr-1.5" />}
                {action.label}
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SmartCardUI;
